import * as React from "react";
import Joi from "joi";
import { Box, Heading } from "@chakra-ui/react";

import useGlobalState from "hooks/useGlobalState";
import NewCreditCheckApiEndpoint from "utils/creditCheck/api/NewCreditCheck";
import NewSelfCheckApiEndpoint from "utils/creditCheck/api/NewSelfCheck";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import useRouter from "hooks/useRouter";
import useApiEndpoint from "hooks/useApiEndpoint";
import Card from "components/Chakra/Card";
import { useTracking } from "contexts/Tracking";
import AnimatedInput from "components/Chakra/AnimatedInput";
import Button from "components/Chakra/Button";
import CustomLink from "components/Chakra/CustomLink";
import DatePicker from "components/Chakra/DatePicker";
import ResponsiveStack from "components/Chakra/ResponsiveStack";
import { useLoginModal } from "modals/useLoginModal";

const FormSchema = Joi.object({
  firstName: Joi.string().required(),
  lastName: Joi.string().required(),
  birthDay: Joi.string().required(),
});

interface IForm {
  firstName: string;
  lastName: string;
  birthDay: string;
}

const PersonForm: React.FC<{ isSelfCheck: boolean; promo?: string }> = ({
  isSelfCheck,
  promo,
}) => {
  const { user } = useGlobalState();
  const { openModal: openLoginModal } = useLoginModal();
  const { navigateTo } = useRouter();
  const { callEndpoint: newCreditCheckApi } = useApiEndpoint(
    NewCreditCheckApiEndpoint,
  );
  const { callEndpoint: newSelfCheckApi } = useApiEndpoint(
    NewSelfCheckApiEndpoint,
  );
  const { trackEvent } = useTracking();

  const { registerWithError, handleSubmit, errors, setValue, watch } =
    useForm<IForm>(FormSchema, {
      defaultValues: {
        firstName: "",
        lastName: "",
        birthDay: "1995-05-01",
      },
    });

  const handleCreditCheck = async ({
    firstName,
    lastName,
    birthDay,
  }: IForm): Promise<void> => {
    if (isSelfCheck) {
      trackEvent({
        "dl.eCat": "CC",
        "dl.eAct": "pay",
        "dl.eLab": "sebeprovereni",
      });
      const { data } = await newSelfCheckApi({
        firstName,
        lastName,
        birthDay,
      });
      if (data?.status === "ok") {
        navigateTo("/sebeprovereni/platba");
      }
    } else {
      trackEvent({
        "dl.eCat": "CC",
        "dl.eAct": "pay",
        "dl.eLab": "provereni",
      });
      const { data } = await newCreditCheckApi({
        firstName,
        lastName,
        birthDay,
      });
      if (data?.status === "ok") {
        navigateTo(
          "/provereni-rizikovosti-osob-backup/platba/[personId]/[orderTypeId]/[promo]",
          {
            personId: data.personId.toString(),
            orderTypeId: data.orderTypeId.toString(),
            promo,
          },
        );
      }
    }
  };

  const handleData: OnFormSubmitType<IForm> = (values) => {
    if (user) {
      handleCreditCheck(values);
    } else {
      openLoginModal({
        onSuccessfulLogin: () => handleCreditCheck(values),
        loginEvent: {
          LOGIN: {
            "dl.eCat": "",
            "dl.eAct": "login",
            "dl.eLab": "SCC – landing",
          },
          REGISTRATION: {
            "dl.eAct": "registration",
            "dl.eLab": "SCC – landing",
            "dl.eCat": "",
          },
        },
      });
    }
  };

  const datePickerValue = watch("birthDay");

  return (
    <Card blendOnMobile>
      <form onSubmit={handleSubmit(handleData)}>
        <Heading as="h3" mb="20px">
          {isSelfCheck ? "Zadejte své údaje" : "Zadejte údaje osoby"}
        </Heading>
        <AnimatedInput
          {...registerWithError("firstName")}
          label="Jméno"
          required
        />
        <AnimatedInput
          {...registerWithError("lastName")}
          label="Příjmení"
          required
        />
        <DatePicker
          name="birthDay"
          value={datePickerValue}
          label="Datum narození"
          onDateSelect={(dd) => {
            setValue("birthDay", dd);
          }}
          error={errors.birthDay}
          customError="global.checkbox.datepicker"
        />
        <ResponsiveStack mb="20px" mt="15px">
          <Button
            type="submit"
            trackEventPayload={
              isSelfCheck
                ? {
                    "dl.eCat": "selfcheck",
                    "dl.eAct": "scc.clicked",
                    "dl.eLab": "Prověřit se",
                  }
                : undefined
            }
          >
            {isSelfCheck
              ? `Prověřit se${promo ? " s promokódem" : ""}`
              : `Ověřit údaje${promo ? " s promokódem" : ""}`}
          </Button>
        </ResponsiveStack>
        <Box
          fontSize="0.75rem"
          fontWeight="normal"
          lineHeight="1.5"
          letterSpacing="normal"
          color="ud-grey-600"
          fontStyle="italic"
          textAlign="center"
        >
          Odesláním informací výslovně jako správce údajů potvrzuji, že jsem se
          seznámil s 
          <CustomLink to="/obchodni-podminky-pro-rk" isExternal showUnderline>
            podmínkami využití služby prověření rizikovosti osob a obsahem
            zpracovatelské smlouvy
          </CustomLink>
           a bez výhrad s těmito podmínkami souhlasím.
        </Box>
      </form>
    </Card>
  );
};

export default PersonForm;
